import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NavController } from '@ionic/angular';
import { first, Subject, takeUntil } from 'rxjs';
import { HeaderDto } from 'src/app/dto/header.dto';
import { Order } from 'src/app/dto/order.dto';
import { SyncAction } from 'src/app/dto/sync-actions.enum';
import {
  AppModeStatus,
  OfflineModeService
} from 'src/app/services/offline-mode.service';
import { OrderChangesService } from 'src/app/services/order/order-changes.service';
import { OrdersService } from 'src/app/services/order/orders.service';
import { take } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';
import { BackgroundSyncService } from '../../../services/sync/background-sync.service';

/** Задержка перед автопереходом в архив после завершения заявки (ожидание формирования документов). */
const NAVIGATION_DELAY_MS = 10000;

@Component({
  selector: 'app-end-order',
  templateUrl: './end-order.component.html',
  styleUrls: ['./end-order.component.scss']
})
/**
 * Экран завершения заявки. Онлайн — помечаем заказ завершённым, вызываем {@code finishForwarding} и
 * через задержку уводим в архив (даём серверу сформировать документы). Офлайн — ставим завершение в
 * очередь синхронизации ({@code endOrder}), чтобы отправить при выходе в онлайн. При сбое онлайн-вызова
 * {@code orders.service} сам ставит завершение в очередь (идемпотентно), поэтому потери нет.
 */
export class EndOrderComponent implements OnInit, OnDestroy {
  targetUrl = window.location.pathname.split('/')[1];
  orderId: string = '';
  request: Order;
  isOfflineMode: boolean;
  dayEndInOffline: boolean = false;
  loading: boolean = false;
  endDayConfirmed: boolean = false;
  expeditorDocGenerating: boolean = false;
  totalWeight: number = 0;
  headerDataValues: HeaderDto = {
    title: ''
  };

  private readonly destroy$ = new Subject<void>();
  /** Таймер автоперехода в архив — храним, чтобы отменить при уходе с экрана. */
  private navigationTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private route: ActivatedRoute,
    private navCtrl: NavController,
    private orderChangesService: OrderChangesService,
    private ordersService: OrdersService,
    private offlineModeService: OfflineModeService,
    private backgroundSyncService: BackgroundSyncService
  ) {}

  ngOnInit(): void {
    this.route.params.pipe(first()).subscribe(parameter => {
      this.orderId = parameter['id'];
    });

    this.offlineModeService.appModeStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (mode: AppModeStatus) =>
          (this.isOfflineMode = mode === AppModeStatus.OFFLINE)
      );

    this.orderChangesService
      .getOrderSubject(this.targetUrl)
      .pipe(takeUntil(this.destroy$))
      .subscribe(currentRequest => {
        if (currentRequest) {
          this.request = currentRequest;
          this.headerDataValues.title = `Заявка № ${this.request.id}`;
          this.totalWeight = this.request.getTotalNetWeight();
        }
      });
  }

  ngOnDestroy() {
    if (this.navigationTimer) {
      clearTimeout(this.navigationTimer);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Подтверждение/отмена завершения заявки. При подтверждении: онлайн — завершаем и планируем переход в
   * архив; офлайн — только ставим в очередь синхронизации. При отмене — уходим назад к списку дней.
   * Защита от повторного подтверждения — {@code endDayConfirmed}.
   */
  endOrder(event: boolean): void {
    if (event) {
      // Защита от повторного подтверждения: без неё двойное нажатие дало бы два вызова
      // finishForwarding и два отложенных перехода.
      if (this.endDayConfirmed) {
        return;
      }

      if (!this.isOfflineMode) {
        this.expeditorDocGenerating = true;
        this.endDayConfirmed = true;

        this.request.completeOrder();
        this.ordersService
          .finishForwarding(this.request.id)
          .pipe(take(1))
          .subscribe({
            // orders.service при сбое уже ставит завершение в очередь синхронизации и пробрасывает
            // ошибку — здесь гасим её, чтобы не осталась необработанной, и фиксируем в Sentry.
            error: err => Sentry.captureException(err)
          });

        this.orderChangesService.updateRequests(this.request, this.targetUrl);

        this.navigationTimer = setTimeout(() => {
          this.expeditorDocGenerating = false;
          this.navCtrl.navigateForward(['archived-requests'], {
            queryParams: { status: 'COMPLETED' }
          });
        }, NAVIGATION_DELAY_MS);
      } else {
        this.expeditorDocGenerating = false;
        this.endDayConfirmed = true;
        this.dayEndInOffline = true;
        this.backgroundSyncService.addSyncAction(
          this.request.id,
          SyncAction.endOrder
        );
      }
    } else {
      this.dayEndInOffline = false;
      this.expeditorDocGenerating = false;
      this.endDayConfirmed = false;

      void this.navCtrl.navigateForward(['days']);
    }
  }

  navBack() {
    void this.navCtrl.pop();
  }
}
